import { useMemo } from 'react';

import dayjs, { extend } from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { DUPLICATE_DATE_INCREMENT } from '@pkg/hooks';

import { addTime, formattedDate, getDiff } from '@app/helpers';

extend(utc);
export enum DurationUnit {
    days = 'days',
    hours = 'hours',
    minutes = 'minutes',
}
export type DurationUnits = Record<string, DurationUnit>;
export const nowFormatted = formattedDate();

export const DURATION_FORM_DEFAULT = {
    submissions: 1,
    game: 1,
};
export const DATE_DEFAULT = {
    submission_starts_at: addTime(nowFormatted, 1),
    submissions: addTime(nowFormatted, 1 + DURATION_FORM_DEFAULT.submissions),
    game: addTime(nowFormatted, 1 + DURATION_FORM_DEFAULT.game + DURATION_FORM_DEFAULT.submissions),
};

export const DURATION_UNIT_DEFAULT: DurationUnits = {
    submissions: DurationUnit.days,
    game: DurationUnit.days,
};

export interface DateValues {
    submission_starts_at: string;
    submissions: string;
    game: string;
}

function handleDurationDiff(diff) {
    if (diff >= 1440) {
        return { diffValue: Math.round(diff / 12 / 60), diffUnit: DurationUnit.days };
    }
    if (diff >= 60 && diff < 1440) {
        return { diffValue: Math.round(diff / 60), diffUnit: DurationUnit.hours };
    }
    if (diff < 60) {
        return { diffValue: diff, diffUnit: DurationUnit.minutes };
    }
    return { diffValue: diff, diffUnit: DurationUnit.days };
}

export function useGetDurationDefaults(gameData, isDuplicate) {
    if (!gameData || !isDuplicate) {
        return {
            formDefaults: DURATION_FORM_DEFAULT,
            durationDefaults: DURATION_UNIT_DEFAULT,
        };
    }

    const submissionsDiff = handleDurationDiff(getDiff(gameData.submission_starts_at, gameData.submission_ends_at));
    const gameDiff = handleDurationDiff(getDiff(gameData.submission_ends_at, gameData.ends_at));

    return {
        formDefaults: { submissions: submissionsDiff.diffValue, game: gameDiff.diffValue },
        durationDefaults: { submissions: submissionsDiff.diffUnit, game: gameDiff.diffUnit },
    };
}

export function useGetDuplicateDateDefaults(gameData) {
    const newSubsStart = useMemo(() => {
        const now = formattedDate();
        const newDate = addTime(gameData?.submission_starts_at, DUPLICATE_DATE_INCREMENT || 2);
        if (dayjs(newDate).isBefore(now, 'hour')) {
            return addTime(now, DUPLICATE_DATE_INCREMENT || 2);
        } else {
            return newDate;
        }
    }, [gameData]);

    return {
        submission_starts_at: newSubsStart,
        submission_ends_at: addTime(newSubsStart, DURATION_FORM_DEFAULT.submissions),
        starts_at: addTime(newSubsStart, DURATION_FORM_DEFAULT.submissions),
        ends_at: addTime(newSubsStart, DURATION_FORM_DEFAULT.game + DURATION_FORM_DEFAULT.submissions),
    };
}
